// Exporta para .docx o resumo do dashboard de orçamento, respeitando os
// filtros ativos (categorias, grupos, departamento, meses).
export async function exportOrcamentoDocx({
  filtros,
  totalOrcado,
  totalRealizado,
  detalhe,
  fmt,
}) {
  const {
    Document,
    Packer,
    Paragraph,
    Table,
    TableRow,
    TableCell,
    TextRun,
    WidthType,
    AlignmentType,
    HeadingLevel,
    ShadingType,
    PageOrientation,
  } = await import("docx");

  const cell = (text, { bold = false, right = false, fill, width } = {}) =>
    new TableCell({
      width: width ? { size: width, type: WidthType.PERCENTAGE } : undefined,
      shading: fill
        ? { type: ShadingType.CLEAR, color: "auto", fill }
        : undefined,
      children: [
        new Paragraph({
          alignment: right ? AlignmentType.RIGHT : AlignmentType.LEFT,
          children: [new TextRun({ text: String(text ?? "-"), bold, size: 18 })],
        }),
      ],
    });

  const linhaFiltro = (rotulo, valores) =>
    new Paragraph({
      spacing: { after: 60 },
      children: [
        new TextRun({ text: `${rotulo}: `, bold: true }),
        new TextRun({ text: valores.length ? valores.join(", ") : "Todos" }),
      ],
    });

  const resumo = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: [
          cell("Total Orçado", { bold: true, fill: "E7EEF7", width: 50 }),
          cell(`R$ ${fmt(totalOrcado)}`, { right: true, width: 50 }),
        ],
      }),
      new TableRow({
        children: [
          cell("Total Realizado", { bold: true, fill: "E7EEF7" }),
          cell(`R$ ${fmt(totalRealizado)}`, { right: true }),
        ],
      }),
    ],
  });

  const colunas = [
    ["Grupo", 18],
    ["Categoria", 22],
    ["Departamento", 16],
    ["Lançamentos", 8, true],
    ["Valor Realizado", 12, true],
    ["Valor Orçado", 12, true],
    ["Diferença (Orçado - Realizado)", 12, true],
  ];

  const tabelaDetalhe = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        tableHeader: true,
        children: colunas.map(([t, w, r]) =>
          cell(t, { bold: true, fill: "D9E2F3", width: w, right: !!r }),
        ),
      }),
      ...detalhe.map(
        (d) =>
          new TableRow({
            children: [
              cell(d.grupo),
              cell(d.categoria),
              cell(d.departamento),
              cell(d.qtd, { right: true }),
              cell(fmt(d.valor), { right: true }),
              cell(fmt(d.orcado), { right: true }),
              cell(fmt(d.diferenca), { right: true }),
            ],
          }),
      ),
    ],
  });

  const doc = new Document({
    sections: [
      {
        properties: {
          page: { size: { orientation: PageOrientation.LANDSCAPE } },
        },
        children: [
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            children: [new TextRun("Orçamento x Realizado")],
          }),
          new Paragraph({
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: `Gerado em ${new Date().toLocaleString("pt-BR")}`,
                italics: true,
                size: 18,
              }),
            ],
          }),
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [new TextRun("Filtros")],
          }),
          linhaFiltro("Categorias", filtros.categorias),
          linhaFiltro("Grupos", filtros.grupos),
          linhaFiltro("Departamento", filtros.departamento),
          linhaFiltro("Mês", filtros.meses),
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 240 },
            children: [new TextRun("Totais")],
          }),
          resumo,
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 240 },
            children: [new TextRun(`Detalhe do Realizado por Categoria`)],
          }),
          tabelaDetalhe,
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `orcamento_${new Date().toISOString().slice(0, 10)}.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Mesma estrutura de dados do DOCX, exportada em planilha (2 abas).
export async function exportOrcamentoXlsx({
  filtros,
  totalOrcado,
  totalRealizado,
  detalhe,
}) {
  const XLSX = await import("xlsx");
  const juntar = (v) => (v.length ? v.join(", ") : "Todos");

  const resumo = [
    ["Orçamento x Realizado"],
    [`Gerado em ${new Date().toLocaleString("pt-BR")}`],
    [],
    ["Categorias", juntar(filtros.categorias)],
    ["Grupos", juntar(filtros.grupos)],
    ["Departamento", juntar(filtros.departamento)],
    ["Mês", juntar(filtros.meses)],
    [],
    ["Total Orçado", totalOrcado],
    ["Total Realizado", totalRealizado],
  ];
  const wsResumo = XLSX.utils.aoa_to_sheet(resumo);
  wsResumo["!cols"] = [{ wch: 18 }, { wch: 60 }];

  const wsDet = XLSX.utils.json_to_sheet(
    detalhe.map((d) => ({
      Grupo: d.grupo,
      Categoria: d.categoria,
      Departamento: d.departamento,
      Lançamentos: d.qtd,
      "Valor Realizado": d.valor,
      "Valor Orçado": d.orcado,
      "Diferença (Orçado - Realizado)": d.diferenca,
    })),
  );
  wsDet["!cols"] = [{ wch: 30 }, { wch: 35 }, { wch: 28 }, { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 30 }];
  for (let r = 2; r <= detalhe.length + 1; r++) {
    ["E", "F", "G"].forEach((col) => {
      const c = wsDet[`${col}${r}`];
      if (c) c.z = "#,##0.00";
    });
  }
  ["B9", "B10"].forEach((a) => {
    if (wsResumo[a]) wsResumo[a].z = "#,##0.00";
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");
  XLSX.utils.book_append_sheet(wb, wsDet, "Realizado por Categoria");
  XLSX.writeFile(wb, `orcamento_${new Date().toISOString().slice(0, 10)}.xlsx`);
}
